import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Flag, Footprints } from 'lucide-react';

/**
 * FloatingActionMenu — bottom-right red FAB that expands into a small radial
 * menu of action items. Used on the Home tab so users can quickly start either
 * a personal run or create a new event without going through the Create tab.
 */
export function FloatingActionMenu() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close menu on outside-click / route blur
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('touchstart', onDown);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  function go(path) {
    setOpen(false);
    nav(path);
  }

  return (
    <div ref={ref} className="fixed bottom-20 right-5 z-30 flex flex-col items-end gap-3">
      {open && (
        <>
          <button
            onClick={() => go('/run/start')}
            data-testid="home-fab-personal-run"
            className="flex items-center gap-2 px-4 h-11 rounded-full bg-foreground text-background shadow-lg shadow-foreground/20 text-sm font-semibold animate-in fade-in slide-in-from-bottom-2 duration-150"
          >
            <Footprints className="h-4 w-4" /> Personal run
          </button>
          <button
            onClick={() => go('/events/new')}
            data-testid="home-fab-create-event"
            className="flex items-center gap-2 px-4 h-11 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 text-sm font-semibold animate-in fade-in slide-in-from-bottom-2 duration-150"
          >
            <Flag className="h-4 w-4" /> Create event
          </button>
        </>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="home-fab"
        aria-label={open ? 'Close menu' : 'Open menu'}
        className={`h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform ${
          open ? 'rotate-45' : ''
        }`}
      >
        {open ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
      </button>
    </div>
  );
}
